import React from "react";
import { OrganizationDto } from "../../../common/service/server/dto/OrganizationDto";
import { Button } from "@truevault/aquilo";
import { RouteHelpers, Routes } from "../../root/routes";
import { TruevaultOnboardingProgress } from "../../../common/hooks/useOnboardingProgress";
import {
  isRequestInboxEnabled,
  isTaskListEnabled,
} from "../../components/layout/nav/StayCompliantNav";

const TASKS_SHORTCUT_ICON = (
  <svg width="52" height="52" viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="52" height="52" rx="8" fill="#DFD8F8" />
    <mask
      id="mask0_2921_2097"
      style={{ maskType: "alpha" }}
      maskUnits="userSpaceOnUse"
      x="11"
      y="11"
      width="30"
      height="30"
    >
      <rect x="11" y="11" width="30" height="30" fill="#D9D9D9" />
    </mask>
    <g mask="url(#mask0_2921_2097)">
      <path
        d="M17.9375 34.75L13.5 30.3125L15.25 28.5625L17.9062 31.2188L23.2188 25.9062L24.9688 27.6875L17.9375 34.75ZM17.9375 24.75L13.5 20.3125L15.25 18.5625L17.9062 21.2188L23.2188 15.9062L24.9688 17.6875L17.9375 24.75ZM27.25 32.25V29.75H38.5V32.25H27.25ZM27.25 22.25V19.75H38.5V22.25H27.25Z"
        fill="#726D83"
      />
    </g>
  </svg>
);

const REQUESTS_SHORTCUT_ICON = (
  <svg width="52" height="52" viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="52" height="52" rx="8" fill="#DFD8F8" />
    <mask
      id="mask0_2921_2109"
      style={{ maskType: "alpha" }}
      maskUnits="userSpaceOnUse"
      x="11"
      y="11"
      width="30"
      height="30"
    >
      <rect x="11" y="11" width="30" height="30" fill="#D9D9D9" />
    </mask>
    <g mask="url(#mask0_2921_2109)">
      <path
        d="M16 36C15.3125 36 14.724 35.7552 14.2344 35.2656C13.7448 34.776 13.5 34.1875 13.5 33.5V18.5C13.5 17.8125 13.7448 17.224 14.2344 16.7344C14.724 16.2448 15.3125 16 16 16H36C36.6875 16 37.276 16.2448 37.7656 16.7344C38.2552 17.224 38.5 17.8125 38.5 18.5V33.5C38.5 34.1875 38.2552 34.776 37.7656 35.2656C37.276 35.7552 36.6875 36 36 36H16ZM26 27.25L16 21V33.5H36V21L26 27.25ZM26 24.75L36 18.5H16L26 24.75ZM16 21V18.5V33.5V21Z"
        fill="#726D83"
      />
    </g>
  </svg>
);

const VENDORS_SHORTCUT_ICON = (
  <svg width="52" height="52" viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="52" height="52" rx="8" fill="#DFD8F8" />
    <mask
      id="mask0_3149_3087"
      style={{ maskType: "alpha" }}
      maskUnits="userSpaceOnUse"
      x="11"
      y="11"
      width="30"
      height="30"
    >
      <rect x="11" y="11" width="30" height="30" fill="#D9D9D9" />
    </mask>
    <g mask="url(#mask0_3149_3087)">
      <path
        d="M33.5 39.75V36H29.75V33.5H33.5V29.75H36V33.5H39.75V36H36V39.75H33.5ZM13.5 36V28.5H12.25V26L13.5 19.75H32.25L33.5 26V28.5H32.25V32.25H29.75V28.5H24.75V36H13.5ZM16 33.5H22.25V28.5H16V33.5ZM13.5 18.5V16H32.25V18.5H13.5ZM14.8125 26H30.9375L30.1875 22.25H15.5625L14.8125 26Z"
        fill="#534E65"
      />
    </g>
  </svg>
);

const BILLING_SETTINGS_SHORTCUT_ICON = (
  <svg width="52" height="52" viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="52" height="52" rx="8" fill="#DFD8F8" />
    <path
      stroke="#726D83"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12.75 20.5h24.5M12.75 21.75h24.5m-20.5 6.25h7.5m-7.5 2.75h3.75m-4.75 3.75h18.75a2.75 2.75 0 0 0 2.75-2.75V19.25A2.75 2.75 0 0 0 34.5 16.5h-18.75a2.75 2.75 0 0 0-2.75 2.75v12.5A2.75 2.75 0 0 0 15.75 34.5Z"
    />
  </svg>
);

export function Shortcuts({
  org,
  onboardingProgress,
  vendorCount,
}: {
  org: OrganizationDto;
  onboardingProgress: TruevaultOnboardingProgress;
  vendorCount?: number;
}) {
  return (
    <div className="tw-boundary">
      <h3 className="text-neutral-900 text-[24px] font-medium mt-6">Shortcuts</h3>

      <div className="flex items-stretch" style={{ gap: "16px" }}>
        <ShortcutGridItem
          icon={TASKS_SHORTCUT_ICON}
          title="Open Items"
          countToDisplay={org?.activeTaskCount}
          buttonText="Review To-Do List"
          buttonUrl={Routes.tasks.open}
          enabled={isTaskListEnabled(org, onboardingProgress)}
        />
        <ShortcutGridItem
          icon={REQUESTS_SHORTCUT_ICON}
          title="Open Requests"
          countToDisplay={org?.activeRequestCount}
          buttonText="Go to Request Inbox"
          buttonUrl={Routes.requests.active}
          enabled={isRequestInboxEnabled(org, onboardingProgress)}
        />
        <ShortcutGridItem
          icon={VENDORS_SHORTCUT_ICON}
          title="Vendors"
          countToDisplay={vendorCount}
          buttonText="Go to Vendors"
          buttonUrl={Routes.dataMap.root}
        />
        <ShortcutGridItem
          icon={BILLING_SETTINGS_SHORTCUT_ICON}
          title="Billing"
          buttonText="Billing Settings"
          buttonUrl={RouteHelpers.organization.billing(org.id)}
        />
      </div>
    </div>
  );
}

function ShortcutGridItem({
  icon,
  title,
  countToDisplay,
  buttonText,
  buttonUrl,
  enabled = true,
}: {
  icon: React.ReactNode;
  title?: string;
  countToDisplay?: number;
  buttonText: string;
  buttonUrl: string;
  enabled?: boolean;
}) {
  const upperAligned = countToDisplay ? "items-center" : "items-start";
  return (
    <div
      className={`rounded-[8px] p-4 flex shadow-[0px_2px_12px_0px_#00000033]`}
      style={{ width: "216px", flexDirection: "column", gap: "24px" }}
    >
      <div className={`flex ${upperAligned} h-[57px]`}>
        <div>{icon}</div>
        {title && (
          <div className="ml-2">
            <div className={`text-[14px] text-gray-500 font-medium`}>{title}</div>
            {countToDisplay !== undefined && (
              <div className="text-[24px] text-gray-900 font-bold">{countToDisplay}</div>
            )}
          </div>
        )}
      </div>
      <Button
        size="s"
        color="secondary"
        onClick={() => window.open(buttonUrl, "_self")}
        disabled={!enabled}
      >
        {buttonText}
      </Button>
    </div>
  );
}
